import { LoadingOutlined, WarningTwoTone } from '@ant-design/icons'
import { CollapseProps, Pagination, Skeleton, Spin, Tooltip } from 'antd'
import { convertKeyToLabel, RD_QUERY_LIMIT } from 'features/dynamicOntology'
import {
  RolodexPaginationType,
  usePaginatedQueries,
} from 'features/fde/helpers/usePaginatedQuery'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import type { Dispatch, SetStateAction } from 'react'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

import { ObjectLinkItem } from './ObjectLinkItem'
import { useMultiState } from './useMultiState'

type CollapseItem = NonNullable<CollapseProps['items']>[0] & {
  totalEntries: number
  isLoading: boolean
}

const LinksDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const LinkCount = styled.span`
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.75rem;
  font-weight: 500;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

interface LinkedItemListProps {
  query: RolodexEntryQueryBody | undefined
  currentOrgId: string | undefined
  currentSiteId: string | undefined
}

const LinkedItemList: FC<LinkedItemListProps> = ({
  query,
  currentOrgId,
  currentSiteId,
}) => {
  const { entries, isLoading } = useRolodexEntries(query)
  return isLoading ? (
    <Skeleton active paragraph={{ rows: 4 }} />
  ) : (
    <LinksDiv>
      {entries.map((e) => (
        <ObjectLinkItem
          key={e.id}
          link={e}
          currentOrgId={currentOrgId}
          currentSiteId={currentSiteId}
        />
      ))}
    </LinksDiv>
  )
}

const LinkedItemListWithPaginationConatiner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`

const LinkedItemListWithPagination: FC<
  LinkedItemListProps & {
    pagination: RolodexPaginationType
    setPagination: Dispatch<SetStateAction<RolodexPaginationType>>
    totalEntries: number
  }
> = ({
  query,
  currentOrgId,
  currentSiteId,
  pagination,
  setPagination,
  totalEntries,
}) => {
  const totalPages = Math.ceil(totalEntries / pagination.pageSize)
  return (
    <LinkedItemListWithPaginationConatiner>
      <LinkedItemList
        query={query}
        currentOrgId={currentOrgId}
        currentSiteId={currentSiteId}
      />
      {totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            current={pagination.currentPage}
            total={totalEntries}
            onChange={(page, pageSize) =>
              setPagination({ ...pagination, currentPage: page, pageSize })
            }
          />
        </PaginationContainer>
      )}
    </LinkedItemListWithPaginationConatiner>
  )
}

const TruncatedIndicator = (): React.ReactNode => (
  <Tooltip title={`Too many results - truncated to ${RD_QUERY_LIMIT}`}>
    <WarningTwoTone twoToneColor='orange' />
  </Tooltip>
)

const LinkedItemListHeader: FC<{
  type: string
  itemCount?: number
  isLoading: boolean
  isTruncated?: boolean
}> = ({ type, itemCount, isLoading, isTruncated }) => (
  <PanelHeader>
    <span>{convertKeyToLabel(type)}</span>
    <Spin spinning={isLoading} indicator={<LoadingOutlined spin />}>
      {itemCount && <LinkCount>{itemCount}</LinkCount>}
      {isTruncated && <TruncatedIndicator />}
    </Spin>
  </PanelHeader>
)

export interface PaginatedCollapsibleLinkedItemListProps {
  entryType: RolodexEntryType
  query: RolodexEntryQueryBody
  currentOrgId: string | undefined
  currentSiteId: string | undefined
}

const DEFAULT_PAGINATION: RolodexPaginationType = {
  pageSize: 10,
  currentPage: 1,
}

const createDefaultPagination = (): RolodexPaginationType => ({
  ...DEFAULT_PAGINATION,
})

const noopSetPagination: Dispatch<SetStateAction<RolodexPaginationType>> = () =>
  undefined

export function usePaginatedCollapsibleLinkedItemList(
  params:
    | PaginatedCollapsibleLinkedItemListProps
    | ReadonlyArray<PaginatedCollapsibleLinkedItemListProps>
): ReadonlyArray<CollapseItem> {
  const paramsArray = useMemo(
    () =>
      (Array.isArray(params)
        ? params
        : [params]) as ReadonlyArray<PaginatedCollapsibleLinkedItemListProps>,
    [params]
  )

  const entryTypes = useMemo(
    () => new Set(paramsArray.map((param) => param.entryType)),
    [paramsArray]
  )

  const paginationsByEntryType = useMultiState(
    entryTypes,
    createDefaultPagination
  )

  const paginatedQueryInputs = useMemo(
    () =>
      paramsArray.map(({ query, entryType }) => {
        const pagination =
          paginationsByEntryType.get(entryType)?.[0] ?? DEFAULT_PAGINATION
        return {
          query,
          pagination,
          groupByType: entryType,
        }
      }),
    [paginationsByEntryType, paramsArray]
  )

  const paginatedResults = usePaginatedQueries(paginatedQueryInputs)

  const collapseItems = useMemo(
    () =>
      paramsArray.map(({ entryType, currentOrgId, currentSiteId }, index) => {
        const [pagination, setPagination] = paginationsByEntryType.get(
          entryType
        ) ?? [DEFAULT_PAGINATION, noopSetPagination]
        const result = paginatedResults[index]
        const totalEntries = result?.totalEntries ?? 0
        const queryLoading = result?.isLoading ?? false
        const paginatedQuery = result?.query
        return {
          key: entryType,
          totalEntries,
          isLoading: queryLoading,
          label: (
            <LinkedItemListHeader
              type={entryType}
              itemCount={totalEntries}
              isLoading={queryLoading}
              isTruncated={result?.truncated}
            />
          ),
          children: queryLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <LinkedItemListWithPagination
              query={paginatedQuery}
              currentOrgId={currentOrgId}
              currentSiteId={currentSiteId}
              pagination={pagination}
              setPagination={setPagination}
              totalEntries={totalEntries}
            />
          ),
        }
      }),
    [paramsArray, paginationsByEntryType, paginatedResults]
  )

  return collapseItems
}
